<template>
  <div>
    <!-- Content -->
    <div
      class="w-full h-full flex justify-center pt-0 p-3 mt-[30px] tablet-v:mt-[56px] tablet-h:mt-[87px]"
    >
      <div class="w-full max-w-[440px]">
        <!-- Header -->
        <div
          class="font-secondary font-medium text-[26px] tracking-[-0.33px] mb-10"
        >
          <div class="leading-[30px] text-green-700 mb-1.5">
            Your work. Your rights.
          </div>
          <div class="leading-[30px] text-gray-800">
            Sign in to your Dacr account
          </div>
        </div>

        <!-- Form -->
        <div class="flex flex-col mb-[42px]">
          <div class="mb-[14px]">
            <UiInput
              v-model="email"
              label="Email"
              type="email"
              :error-msg="emailError"
              :disabled="loading"
              :tabindex="1"
            />
          </div>
          <div class="mb-4 tablet-v:mb-5">
            <UiInput
              v-model="password"
              label="Password"
              type="password"
              :error-msg="passwordError"
              :disabled="loading"
              :tabindex="2"
              @submit="handleSignIn"
            />
          </div>
          <div
            class="flex flex-col tablet-h:flex-row-reverse tablet-h:items-center justify-between"
          >
            <UiButton
              class="w-full! tablet-h:w-auto! mb-6 tablet-v:mb-7 tablet-h:mb-0"
              :loading="loading"
              primary
              @click="handleSignIn"
            >
              Sign in
            </UiButton>
            <RouterLink
              to="forgot-password"
              class="font-secondary text-link text-[14px]"
              :class="linkClasses"
            >
              Forgot password?
            </RouterLink>
          </div>
        </div>

        <div class="font-secondary text-[14px] leading-[22px] text-center">
          New to Dacr?
          <span
            class="text-link"
            :class="linkClasses"
            @click="handleCreateAccountClick"
          >
            Create an account
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { UiInput, UiButton } from '@dacr/ui'
import {
  MeDocument,
  useValidateOtpMutation,
  type MeQuery,
} from '@/shared/graphql/legacy/generated'
import { useUserStore } from '@/shared/stores/user'
import { useRoute, useRouter } from 'vue-router'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { signin } from '@dacr/auth'
import { apolloClient } from '@/shared/graphql/client'
import { EMAIL_REGEX } from '@/shared/utils/string'
import { analytics } from '@/shared/services/analytics'

const { mutate: validateOTP } = useValidateOtpMutation()

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const toastStore = useToastStore()

const email = ref('')
const password = ref('')
const loading = ref(false)

const emailError = ref('')
const passwordError = ref('')

watch(email, () => {
  emailError.value = ''
})

watch(password, () => {
  passwordError.value = ''
})

const linkClasses = computed(() => ({
  ...(loading.value && {
    'pointer-events-none': true,
    'opacity-50': true,
  }),
}))

const handleCreateAccountClick = () => {
  router.push({
    path: '/register',
    query: {
      ott: route.query.ott,
      redirect: route.query.redirect,
    },
  })
}

const validate = () => {
  let hasError = false

  if (email.value.trim() && !EMAIL_REGEX.test(email.value.trim())) {
    emailError.value = 'Please enter a valid email.'
    hasError = true
  }

  if (!email.value.trim()) {
    emailError.value = 'Please enter your email address'
    hasError = true
  }

  if (!password.value.trim()) {
    passwordError.value = 'Please enter your password'
    hasError = true
  }

  return !hasError
}

const handleSignIn = async () => {
  if (!validate()) return

  try {
    loading.value = true

    await signin({
      email: email.value,
      password: password.value,
      client: 'web',
    })

    if (route.query.ott) {
      await validateOTP({
        code: route.query.ott as string,
      })

      router.push('/auth/success')
      return
    }

    const { data } = await apolloClient.query<MeQuery>({
      query: MeDocument,
    })

    if (data) {
      window.gtag('event', 'login', {
        user_id: data.me.id,
        name: data.me.name,
        username: data.me.username,
      })

      analytics.track('user_login', {
        id: data.me.id,
        name: data.me.name,
        username: data.me.username,
        email: data.me.email,
      })

      userStore.setUser(data.me)
      userStore.isSignedIn = true

      // Check for OAuth redirect
      const redirectUrl = route.query.redirect as string

      if (redirectUrl) {
        window.location.href = redirectUrl
        return
      }

      router.push('/home')
    }
  } catch (error) {
    console.error(error)

    toastStore.showToast({
      type: ToastType.Error,
      content: 'Incorrect email or password',
      error: error as Error,
    })
  } finally {
    loading.value = false
  }
}
</script>
